import { cloneDeep } from 'lodash'

import store from '@/store/'
import { RouteUtil } from '@/utils/Route'

import { PartnerHelper, StatusHelper } from '@/helpers'

import {
  CHANNEL_ALVA,
  ROUTE_INDEX_APPLICATION_ADMIN_DATA,
  ROUTE_INDEX_APPLICATION_EXISTING_CUSTOMER_DATA,
  ROUTE_INDEX_APPLICATION_HEALTHQUESTIONS,
  ROUTE_INDEX_APPLICATION_PERSONAL_DATA,
  ROUTE_INDEX_BASKET,
  ROUTE_INDEX_ADVISORY_PROTOCOL,
  ROUTE_INDEX_NEXT_STEPS,
  ROUTE_INDEX_OFFERS,
  ROUTE_INDEX_PERSONS,
  ROUTE_INDEX_SIGNATURE_ENTRY,
  ROUTE_INDEX_SIGNATURE_VERIFICATION,
  ROUTE_INDEX_SUBMIT,
  ROUTE_NAME_ADVISORY_PROTOCOL,
  ROUTE_NAME_NEXT_STEPS,
  ROUTE_NAME_PERSONS,
} from '@/config/constants'

import { ROUTE_INDEX_APPLICATION_ONBOARDING } from '@/config/constants'
import { isRequired } from '@/utils/CustomErrors'

export const RouteHelper = {
  ...RouteUtil,

  getBasket: function () {
    return cloneDeep(store._modules.root.state.basket)
  },

  getPartner: function () {
    return cloneDeep(store._modules.root.state.content.partner)
  },

  getNextAideRoute(basket, options) {
    if (options.checkChannel && basket.channel !== CHANNEL_ALVA) return { name: 'aide-entry' }

    if (basket.persons.length === 0) return { name: 'aide-persons' }
  },

  getNextRoute(basket) {
    if (basket.submitted) return ROUTE_NAME_NEXT_STEPS

    if (!StatusHelper.isPersonalDataValid(basket.persons)) return ROUTE_NAME_PERSONS

    const hasProducts = StatusHelper.basketHasProducts(basket.persons)

    if (hasProducts) {
      return 'basket'
    } else {
      return 'offers'
    }
  },

  getNextAvailableRoute(
    $route,
    config = {
      routeIndex: null,
      includeDisabledRoutes: false,
    }
  ) {
    const { basket, shouldValidate } = store.state
    const routes = RouteHelper.getRoutes($route, basket, shouldValidate)

    const currentRoute = RouteHelper.getRouteByIndex(routes, $route.meta.index)
    const startIndex = config.routeIndex ?? currentRoute.index

    return routes.find(r => !r.hidden && (config.includeDisabledRoutes ? true : !r.disabled) && r.index > startIndex)
  },

  getPreviousNonHiddenRoute($route) {
    const { basket, shouldValidate } = store.state
    const routes = RouteHelper.getRoutes($route, basket, shouldValidate)
    const reverseRoutes = cloneDeep(routes)
    reverseRoutes.sort((a, b) => (a.index < b.index ? 1 : -1))

    const currentRoute = RouteHelper.getRouteByIndex(routes, $route.meta.index)
    return reverseRoutes.find(r => !r.hidden && !r.disabled && r.index < currentRoute.index)
  },

  getRoutes($route, basket, shouldValidate) {
    const states = StatusHelper.states(basket)
    const routes = [
      getPersonRoute($route, { basket }, states),
      getOfferRoute($route, { basket }, states),
      getBasketRoute($route, { basket }, states),
      getOnboardingRoute($route, { basket, shouldValidate }, states),
      getExistingCustomerRoute($route, { basket, shouldValidate }, states),
      getPersonalDataRoute($route, { basket, shouldValidate }, states),
      getAdminDataRoute($route, { basket, shouldValidate }, states),
      getHealthquestionRoute($route, { basket, shouldValidate }, states),
      getSubmitRoute($route, {}, states),
      getAdvisoryProtocolRoute($route),
      getSignatureEntryRoute($route, { basket }),
      getSignatureVerificationRoute($route, { basket }),
      getNextStepsRoute($route),
    ]
    return routes.sort((a, b) => (a.index < b.index ? -1 : 1))
  },

  getRouteAfterReentry(basket) {
    if (basket.submitted) return ROUTE_NAME_NEXT_STEPS
    if (!StatusHelper.isPersonalDataValid(basket.persons)) return ROUTE_NAME_PERSONS
    return 'basket'
  },

  getRouteByIndex(routes, routeIndex) {
    return routes.find(r => r.index === routeIndex)
  },
}

function RouteConfig({
  complete = isRequired('complete'),
  disabled = isRequired('disabled'),
  hidden = isRequired('hidden'),
  name = isRequired('name'),
  invalid = isRequired('invalid'),
  isInNavigation = isRequired('isInNavigation'),
  match = isRequired('match'),
  small = isRequired('small'),
  path = isRequired('path'),
  text, // optional
  tooltip, // optional
  index = isRequired('index'),
}) {
  this.complete = complete
  this.disabled = disabled
  this.hidden = hidden
  this.name = name
  this.invalid = invalid
  this.isInNavigation = isInNavigation
  this.match = match
  this.small = small
  this.path = path
  this.text = text
  this.tooltip = tooltip
  this.index = index
}

function getPersonRoute(
  $route,
  { basket = isRequired('basket') },
  { isEntryPersonsValid = isRequired('isEntryPersonsValid') }
) {
  const people = basket.persons.length
  const isExistingCustomer = !!basket.existingCustomer

  return new RouteConfig({
    complete: people > 0,
    disabled: isExistingCustomer,
    hidden: false,
    name: ROUTE_NAME_PERSONS,
    invalid: people > 0 && !isEntryPersonsValid,
    isInNavigation: true,
    match: $route.path.includes('/persons'),
    small: false,
    path: { name: ROUTE_NAME_PERSONS },
    tooltip: isExistingCustomer ? 'temp.navigation.disabled' : undefined,
    index: ROUTE_INDEX_PERSONS,
  })
}

function getOfferRoute(
  $route,
  { basket = isRequired('basket') },
  { isEntryPersonsValid = isRequired('isEntryPersonsValid'), isOfferComplete = isRequired('isOfferComplete') }
) {
  const people = basket.persons.length
  const isExistingCustomer = !!basket.existingCustomer

  return new RouteConfig({
    complete: $route.meta.index > 1 || isOfferComplete,
    disabled: people === 0 || !isEntryPersonsValid || isExistingCustomer,
    hidden: false,
    name: 'products', //TODO products/offers naming ?  other routes have same name and path name
    invalid: false,
    isInNavigation: true,
    match: $route.path.includes('/products'),
    small: false,
    path: {
      name: 'offers',
      params: { personId: basket.persons[0]?.personId },
    },
    tooltip: isExistingCustomer ? 'temp.navigation.disabled' : undefined,
    index: ROUTE_INDEX_OFFERS,
  })
}

function getBasketRoute(
  $route,
  { basket = isRequired('basket') },
  { isEntryPersonsValid = isRequired('isEntryPersonsValid'), hasProducts, isBasketValid, isOfferComplete }
) {
  const metaIndex = $route.meta.index

  const people = basket.persons.length

  return new RouteConfig({
    complete: metaIndex > 2 || isOfferComplete,
    disabled: people === 0 || !isEntryPersonsValid,
    hidden: false,
    name: 'basket',
    invalid: !hasProducts && !isBasketValid,
    isInNavigation: true,
    match: $route.path.includes('/basket'),
    small: false,
    path: { name: 'basket' },
    index: ROUTE_INDEX_BASKET,
  })
}

//TODO !handle A/B Testing with onboarding as modal!
function getOnboardingRoute(
  $route,
  { shouldValidate = isRequired('shouldValidate') },
  {
    hasProducts = isRequired('hasProducts'),
    isAdminDataComplete = isRequired('isAdminDataComplete'),
    isBasketValid = isRequired('isBasketValid'),
    isOfferComplete = isRequired('isOfferComplete'),
    isOnboardingSecured = isRequired('isOnboardingSecured'),
    areHealthQuestionsCompletedForAllPersons = isRequired('areHealthQuestionsCompletedForAllPersons'),
    isPersonDataComplete = isRequired('isPersonDataComplete'),
    isOfferValid = isRequired('isOfferValid'),
  }
) {
  return new RouteConfig({
    complete:
      isOfferComplete || (areHealthQuestionsCompletedForAllPersons && isPersonDataComplete && isAdminDataComplete),
    disabled:
      (!isBasketValid || !hasProducts || ($route.meta.index <= ROUTE_INDEX_BASKET && !isOnboardingSecured)) &&
      $route.meta.index !== ROUTE_INDEX_APPLICATION_ONBOARDING,
    hidden: false,
    name: 'application-onboarding',
    invalid: shouldValidate && !($route.name === 'submit' ? isOfferValid : true),
    isInNavigation: true,
    match: $route.path.includes('/application'),
    small: false,
    path: { name: 'application-onboarding' },
    index: ROUTE_INDEX_APPLICATION_ONBOARDING,
  })
}

function getExistingCustomerRoute(
  $route,
  { basket = isRequired('basket'), shouldValidate = isRequired('shouldValidate') },
  {
    isOnboardingSecured = isRequired('isOnboardingSecured'),
    isPersonDataComplete = isRequired('isPersonDataComplete'),
    isAdminDataComplete = isRequired('isAdminDataComplete'),
  }
) {
  return new RouteConfig({
    complete: isPersonDataComplete && isAdminDataComplete,
    disabled: !isOnboardingSecured,
    hidden: !basket.existingCustomer || $route.meta.index < ROUTE_INDEX_APPLICATION_ONBOARDING,
    name: 'application-existing-customer-data',
    invalid: shouldValidate && (!isPersonDataComplete || !isAdminDataComplete),
    isInNavigation: true,
    match: $route.path.includes('/application/existing-customer-data'),
    small: true,
    path: { name: 'application-existing-customer-data' },
    index: ROUTE_INDEX_APPLICATION_EXISTING_CUSTOMER_DATA,
  })
}

function getPersonalDataRoute(
  $route,
  { basket = isRequired('basket'), shouldValidate = isRequired('shouldValidate') },
  { isPersonDataComplete = isRequired('isPersonDataComplete'), isOnboardingSecured = isRequired('isOnboardingSecured') }
) {
  return new RouteConfig({
    complete: isPersonDataComplete, // rename isComplete in general is and has for booleans
    disabled: !isOnboardingSecured,
    hidden: basket.existingCustomer || $route.meta.index < ROUTE_INDEX_APPLICATION_ONBOARDING || !isOnboardingSecured,
    name: 'application-personal-data',
    invalid: shouldValidate && !isPersonDataComplete,
    isInNavigation: true, //  rename
    match: $route.path.includes('/application/personal-data/'),
    small: true,
    path: {
      name: 'application-personal-data',
      params: { personId: basket.persons[0]?.personId },
    },
    index: ROUTE_INDEX_APPLICATION_PERSONAL_DATA,
  })
}

function getAdminDataRoute(
  $route,
  { basket = isRequired('basket'), shouldValidate = isRequired('shouldValidate') },
  { isAdminDataComplete = isRequired('isAdminDataComplete'), isOnboardingSecured = isRequired('isOnboardingSecured') }
) {
  return new RouteConfig({
    complete: isAdminDataComplete,
    disabled: !isOnboardingSecured,
    hidden: basket.existingCustomer || $route.meta.index < ROUTE_INDEX_APPLICATION_ONBOARDING || !isOnboardingSecured,
    name: 'application-administrative-data',
    invalid: shouldValidate && !isAdminDataComplete,
    isInNavigation: true,
    match: $route.path.includes('/application/administrative-data'),
    small: true,
    path: { name: 'application-administrative-data' },
    index: ROUTE_INDEX_APPLICATION_ADMIN_DATA,
  })
}

function getHealthquestionRoute(
  $route,
  { basket = isRequired('basket'), shouldValidate = isRequired('shouldValidate') },
  {
    areHealthQuestionsCompletedForAllPersons = isRequired('areHealthQuestionsCompletedForAllPersons'),
    isOnboardingSecured = isRequired('isOnboardingSecured'),
  }
) {
  const hasHealthquestions =
    basket.persons.filter(p => p.healthQuestionAnswers && p.healthQuestionAnswers.length > 0).length > 0

  const hidden = !hasHealthquestions || !isOnboardingSecured || $route.meta.index < ROUTE_INDEX_APPLICATION_ONBOARDING
  return new RouteConfig({
    complete: areHealthQuestionsCompletedForAllPersons,
    disabled: !isOnboardingSecured,
    hidden: hidden,
    name: 'application-healthquestions',
    invalid: shouldValidate && !areHealthQuestionsCompletedForAllPersons,
    isInNavigation: true,
    match: $route.path.includes('/application/healthquestions/'),
    small: true,
    path: {
      name: 'application-healthquestions',
      params: { personId: basket.persons[0]?.personId },
    },
    index: ROUTE_INDEX_APPLICATION_HEALTHQUESTIONS,
  })
}

function getSubmitRoute(
  $route,
  _,
  { isOfferComplete = isRequired('isOfferComplete'), isOfferValid = isRequired('isOfferValid') }
) {
  return new RouteConfig({
    complete: isOfferComplete,
    disabled: !isOfferValid,
    hidden: false,
    name: 'submit',
    invalid: false,
    isInNavigation: true,
    match: $route.path.includes('/submit'),
    small: false,
    text: '5',
    path: { name: 'submit' },
    index: ROUTE_INDEX_SUBMIT,
  })
}

function getAdvisoryProtocolRoute($route) {
  const isInvalidFromRoute = $route.meta.index < ROUTE_INDEX_SUBMIT
  const hideProtocol = !PartnerHelper.isBroker(RouteHelper.getPartner())

  return new RouteConfig({
    complete: false, // not shown in navigation, value irrelevant
    disabled: false, // not shown in navigation, value irrelevant
    hidden: hideProtocol || isInvalidFromRoute,
    name: ROUTE_NAME_ADVISORY_PROTOCOL,
    invalid: false, // not shown in navigation, value irrelevant
    isInNavigation: false,
    match: $route.path.includes('/advisory-protocol'),
    small: false, // not shown in navigation, value irrelevant
    path: { name: ROUTE_NAME_ADVISORY_PROTOCOL },
    index: ROUTE_INDEX_ADVISORY_PROTOCOL,
  })
}

function getSignatureEntryRoute($route, { basket = isRequired('basket') }) {
  const isInvalidFromRoute = $route.meta.index < ROUTE_INDEX_SUBMIT

  return new RouteConfig({
    complete: false, // not shown in navigation, value irrelevant
    disabled: false, // not shown in navigation, value irrelevant
    hidden: !basket.signature?.signatureRequired || isInvalidFromRoute,
    name: 'digital-signature-entry',
    invalid: false, // not shown in navigation, value irrelevant
    isInNavigation: false,
    match: $route.path.includes('/digital-signature/entry'),
    small: false, // not shown in navigation, value irrelevant
    path: { name: 'digital-signature-entry' },
    index: ROUTE_INDEX_SIGNATURE_ENTRY,
  })
}

function getSignatureVerificationRoute($route, { basket = isRequired('basket') }) {
  const isInvalidFromRoute = $route.meta.index < ROUTE_INDEX_SUBMIT

  return new RouteConfig({
    complete: false, // not shown in navigation, value irrelevant
    disabled: false, // not shown in navigation, value irrelevant
    hidden: !basket.signature?.signatureRequired || isInvalidFromRoute,
    name: 'digital-signature-verification',
    invalid: false, // not shown in navigation, value irrelevant
    isInNavigation: false,
    match: $route.path.includes('/digital-signature/verification'),
    small: false, // not shown in navigation, value irrelevant
    path: { name: 'digital-signature-verification' },
    index: ROUTE_INDEX_SIGNATURE_VERIFICATION,
  })
}

function getNextStepsRoute($route) {
  // special case reentry allow navigation via meta flag
  const { allowNavigationToNextSteps } = $route.meta
  const isInvalidFromRoute = allowNavigationToNextSteps ? false : $route.meta.index < ROUTE_INDEX_SUBMIT

  return new RouteConfig({
    complete: false, // not shown in navigation, value irrelevant
    disabled: false, // not shown in navigation, value irrelevant
    hidden: isInvalidFromRoute,
    name: ROUTE_NAME_NEXT_STEPS,
    invalid: false, // not shown in navigation, value irrelevant
    isInNavigation: false,
    match: $route.path.includes('/next-steps'),
    small: false, // not shown in navigation, value irrelevant
    path: { name: ROUTE_NAME_NEXT_STEPS },
    index: ROUTE_INDEX_NEXT_STEPS,
  })
}
